import { useLang } from '@/i18n';
import EcoImg from 'eco-design/base/eco-img';
import EcoSpace from 'eco-design/base/eco-space';
import { useEffect, useRef } from 'react';
import { useWebpOrJpg } from '../../hooks/useWebpOrJpg';
import css from './index.module.scss';

const MoreIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M11.9994 2C17.5222 2 21.9994 6.47715 21.9994 12C21.9994 17.5228 17.5222 22 11.9994 22C6.47674 21.9998 1.99939 17.5227 1.99939 12C1.99939 6.47729 6.47674 2.00023 11.9994 2ZM11.892 17.7051C11.3949 17.7051 10.9916 18.1084 10.9916 18.6055C10.9916 19.1025 11.3949 19.5059 11.892 19.5059H11.9027C12.3996 19.5056 12.8031 19.1024 12.8031 18.6055C12.8031 18.1086 12.3996 17.7053 11.9027 17.7051H11.892ZM12.557 5.76465C11.5097 5.58501 10.4321 5.78203 9.51599 6.32031C8.59994 6.85869 7.90386 7.70377 7.55115 8.70605C7.38624 9.17485 7.63225 9.68846 8.10095 9.85352C8.56968 10.0182 9.08345 9.77236 9.24841 9.30371C9.46002 8.70227 9.87842 8.19512 10.4281 7.87207C10.9777 7.54921 11.6241 7.43032 12.2523 7.53809C12.8806 7.64596 13.4512 7.97324 13.8617 8.46094C14.2721 8.94867 14.4964 9.56667 14.4955 10.2041V10.2051C14.4954 10.9272 13.9415 11.5926 13.0961 12.1562C12.7009 12.4196 12.2989 12.6216 11.9926 12.7578C11.8411 12.8251 11.7155 12.8747 11.6302 12.9072C11.5879 12.9233 11.555 12.9361 11.5345 12.9434C11.5248 12.9468 11.5171 12.9488 11.5131 12.9502C11.512 12.9506 11.5107 12.951 11.5101 12.9512H11.5111C11.1437 13.0737 10.896 13.4174 10.8959 13.8047V15.6055C10.8961 16.1023 11.2994 16.5049 11.7963 16.5049C12.2928 16.5044 12.6954 16.102 12.6957 15.6055V14.4141C12.7048 14.4101 12.7148 14.4064 12.724 14.4023C13.0924 14.2386 13.5908 13.9905 14.0951 13.6543C14.9897 13.0578 16.141 12.0227 16.2816 10.5137L16.2963 10.2051C16.2976 9.14298 15.9225 8.11438 15.2386 7.30176C14.5545 6.48899 13.6041 5.94447 12.557 5.76465Z"
            fill="white"
        />
    </svg>
);

const Compare: React.FC = () => {
    const { t } = useLang('dpux.Compare');
    const webpOrJpg = useWebpOrJpg('#delta-pro-ultra-x-Compare');

    const list = [
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1759214143863/Home%20Standby%20Generators.png',
            webp: 'https://websiteoss.ecoflow.com/upload/image/1759214144571/Home.webp',
            title: t('item1_title'),
            items: [
                {
                    value: t('item1_value1'),
                    key: t('item1_key1'),
                },
                {
                    value: t.rich('item1_value2', {
                        t: (v: string) => <span className="top-text">{v}</span>,
                    }),
                    key: t('item1_key2'),
                },
            ],
        },
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1759214143612/DPUX.png',
            webp: 'https://websiteoss.ecoflow.com/upload/image/1759214144305/DPUX.webp',
            title: t('item2_title'),
            items: [
                {
                    value: t('item2_value1'),
                    key: t('item2_key1'),
                    tips: [t('tip')],
                },
                {
                    value: t.rich('item2_value2', {
                        t: (v: string) => <span className="top-text">{v}</span>,
                        s2: (v: string) => <span>{v}</span>,
                        s: (v: string) => <span className="s">{v}</span>,
                    }),
                    key: t('item2_key2'),
                },
            ],
        },
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1759214144063/Traditional%20Residential%20Energy%20Storage%20System.png',
            webp: 'https://websiteoss.ecoflow.com/upload/image/1759214144813/Traditional.webp',
            title: t('item3_title'),
            items: [
                {
                    value: t('item3_value1'),
                    key: t('item3_key1'),
                },
                {
                    value: t.rich('item3_value2', {
                        t: (v: string) => <span className="top-text">{v}</span>,
                    }),
                    key: t('item3_key2'),
                },
            ],
        },
    ];

    /** 动画 */
    const sectionRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!sectionRef.current) return;
        const title = sectionRef.current.querySelector('.compare-title');
        const list = sectionRef.current.querySelector('.compare-list')?.children ?? [];
        const screen = AniContainer.create({
            triggerElement: sectionRef.current,
            triggerHook: 1,
            offset: '20%',
            duration: '100%',
            onEnter: () => {
                title?.classList.add('enter');
                setTimeout(() => {
                    for (let i = 0; i < list.length; i++) {
                        setTimeout(() => {
                            list[i].classList.add('enter');
                        }, 60 * i);
                    }
                }, 100);
            },
            onProcess: (process: number) => {
                if (process <= 0) {
                    title?.classList.remove('enter');
                    for (let i = 0; i < list.length; i++) {
                        list[i].classList.remove('enter');
                    }
                }
            },
        });
        return () => {
            screen.destroy?.();
        };
    }, []);
    return (
        <>
            <EcoSpace />
            <section
                className={`${css.section} delta-pro-ultra-x-Compare GtmModuleData-section`}
                id="delta-pro-ultra-x-Compare"
                data-section-id={'delta-pro-ultra-x-Compare'}
                data-section-title={t('title')}
                ref={sectionRef}
            >
                <div className="compare-title ani-1">{t('title')}</div>
                <div className="compare-list">
                    {list.map((i, idx) => (
                        <div className={`compare-card ani-1 ${idx === 1 ? 'highlight' : ''}`} key={i.title}>
                            <div className="compare-card-top">
                                <EcoImg pc={webpOrJpg(i.img, i.webp)} alt={i.title} />
                                <div className="compare-card-title">{i.title}</div>
                            </div>
                            <div className="compare-card-items">
                                {i.items.map((j, idx2) => (
                                    <div className="compare-card-item" key={j.key + i.title}>
                                        <div className="compare-card-item-value">
                                            {j.value}
                                            {/* @ts-ignore */}
                                            {j.tips && (
                                                <div className="tips-box">
                                                    <div className="tips-icon">
                                                        <MoreIcon />
                                                    </div>
                                                    <div className="tips-content">
                                                        {/* @ts-ignore */}
                                                        {j.tips.map((tip, index) => (
                                                            <div key={index}>{tip}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="compare-card-item-key">{j.key}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
};

export default Compare;
